import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { MdVerified } from 'react-icons/md';
import { FaArrowLeft, FaCalendarCheck } from 'react-icons/fa';
import type { IconBaseProps, IconType } from 'react-icons';
import Header from '../../components/layout/Header';
import '../../styles/Services.css';
import { useCurrency } from '../../context/CurrencyContext';
import { getServicePlans } from '../../data/servicePlans';
import { getServicePriceSnapshot } from '../../utils/servicePricing';
import { trackMatomoEvent } from '../../utils/matomo';

const asIconComponent = (Icon: IconType, name: string) => {
  const Comp: React.FC<IconBaseProps> = (props) => {
    const element = Icon(props);
    return React.isValidElement(element) ? element : null;
  };
  Comp.displayName = name;
  return Comp;
};

const ArrowLeftIcon = asIconComponent(FaArrowLeft, 'ArrowLeftIcon');
const CalendarIcon = asIconComponent(FaCalendarCheck, 'CalendarIcon');
const VerifiedIcon = asIconComponent(MdVerified, 'VerifiedIcon');

const renderInlineStrongText = (item: string) => {
  const parts = item.split('**');

  if (parts.length < 3) {
    return item;
  }

  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <strong key={`${part}-${index}`}>{part}</strong>
        ) : (
          part
        ),
      )}
    </>
  );
};

const renderListItemText = (item: string) => {
  const separatorIndex = item.indexOf(':');

  if (separatorIndex === -1) {
    return item;
  }

  const label = item.slice(0, separatorIndex + 1);
  const description = item.slice(separatorIndex + 1);

  return (
    <span>
      <strong>{label}</strong>
      {description}
    </span>
  );
};

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const { i18n, t } = useTranslation();
  const { currency } = useCurrency();
  const specialistRequestHref =
    process.env.REACT_APP_SPECIALIST_REQUEST_URL ??
    'https://bluealps.yayforms.link/ak5YjNg';
  const bookingHref =
    'https://outlook.office.com/book/BlueAlps@bluealps.org/?ismsaljsauthenabled';
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const plans = getServicePlans(t);
  const plan = plans.find((item) => item.key === serviceId);

  if (!plan) {
    return (
      <>
        <Header />
        <Container className="services-bg">
          <section className="service-detail-empty">
            <h1>{t('pagenotfound.notFound')}</h1>
            <Link to="/services" className="btn-lg btn-gradient">
              {t('services.back_to_services')}
            </Link>
          </section>
        </Container>
      </>
    );
  }

  const isSpecialistRequest = plan.key !== 'audit';
  const pricing = getServicePriceSnapshot(plan.key, {
    currency,
    locale,
  });
  const renderBookingButton = (eventLabel: string) => (
    <a
      href={bookingHref}
      className="btn-lg btn-gradient"
      target="_blank"
      rel="noopener noreferrer"
      onClick={() =>
        trackMatomoEvent('Contact', 'click', `${eventLabel} ${plan.key}`)
      }
    >
      <CalendarIcon aria-hidden />
      {t('services.detail_schedule_cta')}
    </a>
  );

  return (
    <>
      <Header />
      <Container className="services-bg">
        <main className="service-detail-page">
          <Link to="/services" className="service-detail-back">
            <ArrowLeftIcon aria-hidden />
            {t('services.back_to_services')}
          </Link>

          <section className="service-detail-hero">
            <div
              className={`service-detail-copy${
                plan.recommended && plan.recommendedLabel
                  ? ' service-detail-copy--has-badge'
                  : ''
              }`}
            >
              {plan.recommended && plan.recommendedLabel && (
                <span className="recommended-badge service-detail-badge">
                  {plan.recommendedLabel}
                </span>
              )}

              <div className="plan-header">
                <div className="plan-title-stack">
                  <h1 className="service-detail-title">{plan.title}</h1>
                  {plan.titleNote && (
                    <p className="plan-title-note">{plan.titleNote}</p>
                  )}
                </div>
              </div>

              <div className="service-detail-topic">
                <span>{t('services.detail_what_is_title')}</span>
                <p>{plan.overview}</p>
              </div>

              <div className="service-detail-actions service-detail-actions--hero">
                <a
                  href={
                    isSpecialistRequest ? specialistRequestHref : '/contact'
                  }
                  className={plan.ctaClassName}
                  target={isSpecialistRequest ? '_blank' : undefined}
                  rel={isSpecialistRequest ? 'noopener noreferrer' : undefined}
                  onClick={() =>
                    trackMatomoEvent(
                      'CTA',
                      'click',
                      `Service detail ${plan.key}`,
                    )
                  }
                >
                  {plan.ctaLabel}
                </a>
              </div>
            </div>

            <div className="service-detail-side">
              <aside className="service-detail-summary">
                <h2>{t('services.detail_value_title')}</h2>
                <div className="service-detail-value-row">
                  {pricing.hasConfiguredPrice && (
                    <div className="service-detail-price">
                      <span>{t('services.plan_price_from')}</span>
                      <strong>{pricing.formattedPrice}</strong>
                    </div>
                  )}
                </div>
                <p>{t('services.detail_discount_note')}</p>
                {renderBookingButton('Service detail booking')}
              </aside>
            </div>
          </section>

          <div
            className={`service-detail-media service-detail-media--${plan.key}`}
            aria-hidden
          />

          <section className="service-detail-content-grid">
            <div className="service-detail-section service-detail-section--wide service-detail-section--included">
              <h2>{t('services.detail_deliverables_title')}</h2>
              <div className="service-detail-feature-grid">
                {plan.features.map((feature, index) => (
                  <div className="service-detail-feature" key={index}>
                    <span className="feature-icon" aria-hidden>
                      <VerifiedIcon />
                    </span>
                    <p>{feature.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <section className="service-detail-booking-strip">
              <div>
                <span>{t('services.detail_booking_strip_label')}</span>
                <h2>{t('services.detail_booking_strip_title')}</h2>
                <p>{t('services.detail_booking_strip_text')}</p>
              </div>
              {renderBookingButton('Service detail booking strip')}
            </section>

            <section className="service-detail-panel service-detail-panel--combined">
              <div className="service-detail-subsection">
                <h2>{t('services.detail_ideal_client_title')}</h2>
                <p>{renderInlineStrongText(plan.idealClient)}</p>
              </div>

              <div className="service-detail-subsection">
                <h2>{t('services.detail_outcomes_title')}</h2>
                <ul className="service-detail-list">
                  {plan.outcomes.map((item) => (
                    <li key={item}>{renderListItemText(item)}</li>
                  ))}
                </ul>
              </div>

              <div className="service-detail-subsection">
                <h2>{t('services.detail_when_to_use_title')}</h2>
                <ul className="service-detail-list">
                  {plan.whenToUse.map((item) => (
                    <li key={item}>{renderListItemText(item)}</li>
                  ))}
                </ul>
              </div>
            </section>

            <section className="service-detail-section service-detail-section--wide service-detail-section--timeline">
              <h2>{t('services.detail_workflow_title')}</h2>
              <div className="service-detail-steps">
                {plan.workflow.map((item, index) => {
                  const stepName =
                    plan.workflowTitles[index] ??
                    t('services.detail_timeline_period', {
                      count: index + 1,
                    });

                  return (
                    <div className="service-detail-step" key={item}>
                      <strong className="service-detail-step-name">
                        {index + 1}
                      </strong>
                      <div className="service-detail-step-bar">
                        <i aria-hidden />
                      </div>
                      <div className="service-detail-step-copy">
                        <h3>{stepName}</h3>
                        <p>{item}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="service-detail-booking-reasons">
              <div className="service-detail-booking-reasons__copy">
                <span>{t('services.detail_booking_reasons_label')}</span>
                <h2>{t('services.detail_booking_reasons_title')}</h2>
                <p>{t('services.detail_booking_reasons_text')}</p>
              </div>
              <div className="service-detail-booking-reasons__cards">
                <article>
                  <strong>01</strong>
                  <h3>{t('services.detail_booking_reason_1_title')}</h3>
                  <p>{t('services.detail_booking_reason_1_text')}</p>
                </article>
                <article>
                  <strong>02</strong>
                  <h3>{t('services.detail_booking_reason_2_title')}</h3>
                  <p>{t('services.detail_booking_reason_2_text')}</p>
                </article>
                <article>
                  <strong>03</strong>
                  <h3>{t('services.detail_booking_reason_3_title')}</h3>
                  <p>{t('services.detail_booking_reason_3_text')}</p>
                </article>
              </div>
              <div className="service-detail-booking-reasons__action">
                {renderBookingButton('Service detail booking reasons')}
              </div>
            </section>

            <section className="service-detail-section service-detail-section--wide">
              <h2>{t('services.detail_faq_title')}</h2>
              <div className="service-detail-faq-list">
                {plan.faq.map((item, index) => (
                  <details
                    className="service-detail-faq-item"
                    key={item.question}
                  >
                    <summary>
                      <span>{String(index + 1).padStart(2, '0')}</span>
                      {item.question}
                    </summary>
                    <p>{item.answer}</p>
                  </details>
                ))}
              </div>
            </section>

            <section className="service-detail-final-cta">
              <span>{t('services.detail_final_cta_label')}</span>
              <h2>{t('services.detail_final_cta_title')}</h2>
              <p>{t('services.detail_final_cta_text')}</p>
              {renderBookingButton('Service detail final booking')}
            </section>
          </section>
        </main>
      </Container>
    </>
  );
};

export default ServiceDetail;
